// What the latest eligibility check means for the banner and the check-in panel
// (docs/plans/phase1-sha.md §6.5; checklist 10 and 12). Pure: the check and the time are
// passed in. Scheme and status names are SHA's, as returned; the states are ours.

import { formatDay, parseIsoDay } from '../time.ts';

export type EligibilityCheckMode = 'online' | 'recorded_by_hand';
export type EligibilityCheckOutcome = 'answered' | 'unavailable' | 'not_configured' | 'refused';
export type EligibilitySchemeOutcome = 'covered' | 'not_covered' | 'unknown';

export interface EligibilitySchemeFacts {
  readonly schemeDisplay: string;
  readonly statusDisplay: string;
  readonly outcome: EligibilitySchemeOutcome;
  /** `YYYY-MM-DD`, as returned. */
  readonly coverageEnd: string | null;
  readonly waitingPeriodEnds: string | null;
}

export interface EligibilityCheckFacts {
  readonly checkedAt: Date;
  readonly mode: EligibilityCheckMode;
  readonly outcome: EligibilityCheckOutcome;
  readonly schemes: readonly EligibilitySchemeFacts[];
}

export const ELIGIBILITY_SUMMARY_STATES = [
  'not_checked',
  'check_failed',
  'stale',
  'covered',
  'waiting_period',
  'unknown',
  'not_covered',
] as const;

export type EligibilitySummaryState = (typeof ELIGIBILITY_SUMMARY_STATES)[number];

export interface EligibilitySummary {
  readonly state: EligibilitySummaryState;
  /** One short sentence for the banner, e.g. "SHIF covered". */
  readonly words: string;
  /** One line per scheme, in the order SHA returned them. */
  readonly schemeLines: readonly string[];
  readonly coveredSchemes: readonly string[];
  readonly recordedByHand: boolean;
}

function day(text: string | null): string | null {
  if (text === null) return null;
  const parsed = parseIsoDay(text);
  return parsed.ok ? formatDay(parsed.value) : text;
}

function joinNames(names: readonly string[]): string {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1] ?? ''}`;
}

function schemeLine(s: EligibilitySchemeFacts): string {
  if (s.outcome === 'covered') {
    const until = day(s.coverageEnd);
    return until === null
      ? `${s.schemeDisplay}: covered`
      : `${s.schemeDisplay}: covered until ${until}`;
  }
  const waiting = day(s.waitingPeriodEnds);
  if (waiting !== null) return `${s.schemeDisplay}: waiting period until ${waiting}`;
  if (s.outcome === 'unknown') return `${s.schemeDisplay}: ${s.statusDisplay}`;
  return `${s.schemeDisplay}: not covered (${s.statusDisplay})`;
}

export function summariseEligibility(
  check: EligibilityCheckFacts | null,
  options: { at: Date; maxAgeMinutes: number },
): EligibilitySummary {
  const base = {
    schemeLines: [] as string[],
    coveredSchemes: [] as string[],
    recordedByHand: false,
  };
  if (check === null) return { ...base, state: 'not_checked', words: 'Cover not checked' };
  const recordedByHand = check.mode === 'recorded_by_hand';
  if (check.outcome !== 'answered') {
    return { ...base, recordedByHand, state: 'check_failed', words: 'Cover check failed' };
  }
  const schemeLines = check.schemes.map(schemeLine);
  const coveredSchemes = check.schemes
    .filter((s) => s.outcome === 'covered')
    .map((s) => s.schemeDisplay);
  const common = { schemeLines, coveredSchemes, recordedByHand };
  const ageMinutes = (options.at.getTime() - check.checkedAt.getTime()) / 60_000;
  if (ageMinutes > options.maxAgeMinutes)
    return { ...common, state: 'stale', words: 'Check not current' };
  if (coveredSchemes.length > 0) {
    const words = `${joinNames(coveredSchemes)} covered${recordedByHand ? ' (recorded by hand)' : ''}`;
    return { ...common, state: 'covered', words };
  }
  const waiting = check.schemes.find((s) => s.waitingPeriodEnds !== null);
  if (waiting !== undefined) {
    return {
      ...common,
      state: 'waiting_period',
      words: `Waiting period until ${day(waiting.waitingPeriodEnds) ?? ''}`,
    };
  }
  const unknown = check.schemes.find((s) => s.outcome === 'unknown');
  if (unknown !== undefined) {
    return { ...common, state: 'unknown', words: `SHA answered: ${unknown.statusDisplay}` };
  }
  return { ...common, state: 'not_covered', words: 'Not covered' };
}
